import React, { useState, useEffect, useCallback } from 'react';
import { _T, C } from '../../../shared/theme';
import { s_PB, s_GB, s_GC, s_PL } from '../../../shared/styles';
import { saveGameScore } from '../../../shared/utils';
import MultiplayerInviteModal from '../../../modals/MultiplayerInviteModal';
import MultiplayerResults from '../../../modals/MultiplayerResults';

// Social recognition game — identify emotions from face emojis + descriptors.
// Multiplayer: both see same prompts, whoever answers faster wins the round.

const EMOTIONS = [
  { emoji: '😢', label: { nl: 'Verdriet',    en: 'Sadness'   }, clues: { nl: ['neerslachtig', 'huilt', 'verlies'], en: ['downcast', 'crying', 'loss'] } },
  { emoji: '😡', label: { nl: 'Woede',       en: 'Anger'     }, clues: { nl: ['gefrustreerd', 'stoom', 'vuurrood'], en: ['frustrated', 'fuming', 'red-faced'] } },
  { emoji: '😨', label: { nl: 'Angst',       en: 'Fear'      }, clues: { nl: ['bevroren', 'hart klopt', 'vlucht'], en: ['frozen', 'heart races', 'run away'] } },
  { emoji: '😊', label: { nl: 'Vreugde',     en: 'Joy'       }, clues: { nl: ['straalt', 'lacht', 'energiek'], en: ['glowing', 'laughing', 'energised'] } },
  { emoji: '😮', label: { nl: 'Verbazing',   en: 'Surprise'  }, clues: { nl: ['ogen groot', 'adem ingehouden', 'onverwacht'], en: ['eyes wide', 'breathless', 'unexpected'] } },
  { emoji: '🤢', label: { nl: 'Walging',     en: 'Disgust'   }, clues: { nl: ['afkeer', 'neus dicht', 'vies'], en: ['repelled', 'nose pinch', 'gross'] } },
  { emoji: '😌', label: { nl: 'Kalmte',      en: 'Calm'      }, clues: { nl: ['rustig', 'adem diep', 'ontspannen'], en: ['peaceful', 'deep breath', 'relaxed'] } },
  { emoji: '🥰', label: { nl: 'Liefde',      en: 'Love'      }, clues: { nl: ['warmte', 'verbonden', 'hart zwelt'], en: ['warmth', 'connected', 'heart swells'] } },
  { emoji: '😤', label: { nl: 'Trots',       en: 'Pride'     }, clues: { nl: ['hoofd hoog', 'borst vooruit', 'prestatie'], en: ['head high', 'chest out', 'achievement'] } },
  { emoji: '😔', label: { nl: 'Schaamte',    en: 'Shame'     }, clues: { nl: ['hoofd omlaag', 'verbergt', 'rood worden'], en: ['head down', 'hiding', 'blushing'] } },
];

const ROUNDS = 8;

function shuffle(arr) { return [...arr].sort(() => Math.random() - 0.5); }

function buildRound(correctEmotion) {
  const distractors = shuffle(EMOTIONS.filter(e => e !== correctEmotion)).slice(0, 3);
  return shuffle([correctEmotion, ...distractors]);
}

function EmotionMatchGame(props) {
  const { lang, onBack, allUsers, friends, currentUserId, userName, invitePartner } = props;
  const nl = lang === 'nl';
  const [partner, setPartner] = useState(null);
  const [showInvite, setShowInvite] = useState(false);
  const [round, setRound] = useState(0);
  const [score, setScore] = useState(0);
  const [streak, setStreak] = useState(0);
  const [correct, setCorrect] = useState(null);
  const [options, setOptions] = useState([]);
  const [clues, setClues] = useState([]);
  const [selected, setSelected] = useState(null);
  const [feedback, setFeedback] = useState(null); // 'correct' | 'wrong'
  const [done, setDone] = useState(false);
  const [showResults, setShowResults] = useState(null);
  const [started, setStarted] = useState(false);

  useEffect(() => {
    if (invitePartner) setPartner(invitePartner);
  }, [invitePartner]); // eslint-disable-line

  useEffect(() => {
    if (props.onRealPartner) props.onRealPartner(null);
  }, []); // eslint-disable-line

  const newRound = useCallback((roundIdx) => {
    const emotionPool = shuffle(EMOTIONS);
    const target = emotionPool[roundIdx % EMOTIONS.length];
    const opts = buildRound(target);
    const clueList = shuffle(target.clues[nl ? 'nl' : 'en']).slice(0, 2);
    setCorrect(target);
    setOptions(opts);
    setClues(clueList);
    setSelected(null);
    setFeedback(null);
  }, [nl]);

  const start = () => { setRound(0); setScore(0); setStreak(0); setDone(false); setShowResults(null); setStarted(true); newRound(0); };

  const pick = (emotion) => {
    if (selected) return;
    setSelected(emotion);
    const isCorrect = emotion === correct;
    setFeedback(isCorrect ? 'correct' : 'wrong');
    const pts = isCorrect ? (10 + Math.min(streak * 2, 20)) : 0;
    const newScore = score + pts;
    const newStreak = isCorrect ? streak + 1 : 0;
    setScore(newScore);
    setStreak(newStreak);

    setTimeout(() => {
      const nextRound = round + 1;
      if (nextRound >= ROUNDS) {
        setDone(true);
        saveGameScore('emotionmatch', newScore);
        if (partner) {
          const ps = Math.floor(Math.random() * 30) + 50;
          setShowResults({ myScore: newScore, partnerScore: ps });
        } else {
          setShowResults({ myScore: newScore, partnerScore: null });
        }
      } else {
        setRound(nextRound);
        newRound(nextRound);
      }
    }, 1000);
  };

  if (showResults) {
    return (
      <MultiplayerResults
        lang={lang}
        myScore={showResults.myScore}
        myLabel={nl ? 'emotie punten' : 'emotion points'}
        partnerId={partner?.id}
        partnerScore={showResults.partnerScore}
        partnerLabel={nl ? 'emotie punten' : 'emotion points'}
        aiComment={nl ? 'Emoties herkennen is een superkracht. Goed gedaan!' : 'Recognising emotions is a superpower. Well done!'}
        onClose={onBack}
        onRematch={start}
      />
    );
  }

  return (
    <div style={{ textAlign: 'center' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <button onClick={() => onBack()} style={{ background: 'none', border: 'none', color: C.silver, cursor: 'pointer', fontSize: 20 }}>←</button>
        <div>
          <div style={{ fontFamily: 'Georgia,serif', fontSize: 18, color: C.white }}>🎭 {nl ? 'Emotie Match' : 'Emotion Match'}</div>
          <div style={{ color: C.silver, fontSize: 11 }}>{nl ? 'Herken de emotie' : 'Identify the emotion'}</div>
        </div>
        {!partner && !started && (
          <button onClick={() => setShowInvite(true)} style={{ background: `${_T.accent}18`, border: `1px solid ${_T.accent}44`, borderRadius: 10, padding: '6px 12px', cursor: 'pointer', color: _T.accent, fontSize: 11 }}>
            + {nl ? 'Partner' : 'Partner'}
          </button>
        )}
        {partner && <div style={{ fontSize: 12, color: C.teal }}>👤 {partner.name}</div>}
        {!partner && started && <div style={{ width: 60 }} />}
      </div>

      {!started ? (
        <div>
          <div style={{ fontSize: 48, marginBottom: 16 }}>🎭</div>
          <div style={{ color: C.silver, fontSize: 13, marginBottom: 24, lineHeight: 1.6 }}>
            {nl ? `${ROUNDS} rondes. Herken de emotie uit de aanwijzingen en de opties.` : `${ROUNDS} rounds. Identify the emotion from the clues and options.`}
          </div>
          <button style={s_PB({ width: '100%', fontSize: 15 })} onClick={start}>▶ {nl ? 'Starten' : 'Start'}</button>
        </div>
      ) : (
        <div>
          {/* Round + score header */}
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
            <span style={{ color: C.silver, fontSize: 12 }}>{nl ? 'Ronde' : 'Round'} {round + 1}/{ROUNDS}</span>
            <span style={{ color: _T.accent, fontSize: 12, fontWeight: 700 }}>⭐ {score}</span>
            {streak >= 2 && <span style={{ color: '#D4873A', fontSize: 12 }}>🔥 ×{streak}</span>}
          </div>

          {/* Big emoji */}
          <div style={{ fontSize: 72, marginBottom: 16 }}>{correct?.emoji}</div>

          {/* Clues */}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginBottom: 20 }}>
            {clues.map((c, i) => (
              <span key={i} style={{ fontSize: 12, background: _T.bg4, border: `1px solid ${_T.cardBorderBot}`, borderRadius: 20, padding: '4px 12px', color: C.silver2 }}>{c}</span>
            ))}
          </div>

          {/* Options grid */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 16 }}>
            {options.map((e, i) => {
              const isSelected = selected === e;
              const isCorrectOption = e === correct;
              let bg = _T.bg4, border = _T.cardBorderBot, color = C.white;
              if (isSelected && feedback === 'correct') { bg = '#2A9E8222'; border = '#2A9E82'; color = '#2A9E82'; }
              else if (isSelected && feedback === 'wrong') { bg = '#C0505022'; border = '#C05050'; color = '#C05050'; }
              else if (selected && isCorrectOption) { bg = '#2A9E8222'; border = '#2A9E82'; color = '#2A9E82'; }
              return (
                <button key={i} onClick={() => pick(e)} disabled={!!selected} style={{ background: bg, border: `1px solid ${border}`, borderRadius: 14, padding: '14px 10px', cursor: selected ? 'default' : 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                  <span style={{ fontSize: 28 }}>{e.emoji}</span>
                  <span style={{ fontSize: 12, color, fontWeight: 600 }}>{nl ? e.label.nl : e.label.en}</span>
                </button>
              );
            })}
          </div>

          {feedback && (
            <div style={{ color: feedback === 'correct' ? '#2A9E82' : '#C05050', fontSize: 14, fontWeight: 700 }}>
              {feedback === 'correct' ? (nl ? '✓ Goed!' : '✓ Correct!') : (nl ? '✗ Helaas' : '✗ Wrong')}
            </div>
          )}
        </div>
      )}

      {showInvite && (
        <MultiplayerInviteModal
          lang={lang}
          title={nl ? 'Emotie Match' : 'Emotion Match'}
          icon="🎭"
          allUsers={allUsers}
          friends={friends}
          onInvite={(u) => { setPartner(u); setShowInvite(false); }}
          onClose={() => setShowInvite(false)}
        />
      )}
    </div>
  );
}

export default EmotionMatchGame;
